import type { RailRegisteredResource } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailSelectionContext";
import type { UnifiedItem } from "@/pages/workspaces/types";

export function toUnifiedItems(
  resources: RailRegisteredResource[],
): UnifiedItem[] {
  const items: UnifiedItem[] = [];
  for (const r of resources) {
    if (r.kind === "skill") continue;
    if (r.kind === "canvas") items.push({ kind: "canvas", data: r.data });
    else if (r.kind === "chat") items.push({ kind: "chat", data: r.data });
    else items.push({ kind: "file", data: r.data });
  }
  return items;
}

export type WorkspacesRailSelectionCapabilities = {
  canOpen: boolean;
  canMove: boolean;
  canDelete: boolean;
  workspaceId: string | null;
  hasChats: boolean;
};

/**
 * Bulk-action gates for Workspaces-rail multi-select.
 *
 * Move requires a single shared workspace and no chats. "Shared with me" files
 * (`sharedSource`) are copy-only in drag/DnD — never offer bulk move for them,
 * even when every selected row happens to share one `workspace_id`.
 */
export function getWorkspacesRailSelectionCapabilities(
  resources: RailRegisteredResource[],
): WorkspacesRailSelectionCapabilities {
  const selectedItems = toUnifiedItems(resources);
  const count = selectedItems.length;
  const hasChats = selectedItems.some((i) => i.kind === "chat");
  const hasSharedSource = resources.some(
    (r) => r.kind === "file" && r.sharedSource,
  );
  const workspaceIds = new Set<string>();
  for (const item of selectedItems) {
    if (item.kind === "canvas" && item.data.workspace_id) {
      workspaceIds.add(item.data.workspace_id);
    } else if (item.kind === "file" && item.data.workspace_id) {
      workspaceIds.add(item.data.workspace_id);
    } else if (item.kind === "chat" && item.data.flow_session?.workspace_id) {
      workspaceIds.add(item.data.flow_session.workspace_id);
    }
  }
  const sameWorkspace = workspaceIds.size === 1;
  return {
    canOpen: count === 1,
    canMove: count > 0 && !hasChats && sameWorkspace && !hasSharedSource,
    canDelete: count > 0,
    workspaceId: sameWorkspace ? [...workspaceIds][0]! : null,
    hasChats,
  };
}
